import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@repo/ui/lib/utils';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const pad = (value: number) => String(value).padStart(2, '0');
const toKey = (year: number, month: number, day: number) =>
  `${year}-${pad(month + 1)}-${pad(day)}`;

export interface CalendarProps {
  /** yyyy-MM-dd dates that already have a Pic of the Day */
  assignedDates: Set<string>;
  /** yyyy-MM-dd */
  selectedDate: string | null;
  onSelect: (date: string) => void;
  minDate: Date;
  maxDate: Date;
}

export const Calendar = ({
  assignedDates,
  selectedDate,
  onSelect,
  minDate,
  maxDate,
}: CalendarProps) => {
  const initial = selectedDate ? new Date(`${selectedDate}T00:00:00`) : minDate;
  const [view, setView] = useState({
    year: initial.getFullYear(),
    month: initial.getMonth(),
  });

  const minKey = toKey(
    minDate.getFullYear(),
    minDate.getMonth(),
    minDate.getDate(),
  );
  const maxKey = toKey(
    maxDate.getFullYear(),
    maxDate.getMonth(),
    maxDate.getDate(),
  );

  const minMonthIndex = minDate.getFullYear() * 12 + minDate.getMonth();
  const maxMonthIndex = maxDate.getFullYear() * 12 + maxDate.getMonth();
  const viewMonthIndex = view.year * 12 + view.month;

  const canPrev = viewMonthIndex > minMonthIndex;
  const canNext = viewMonthIndex < maxMonthIndex;

  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const firstOffset = (new Date(view.year, view.month, 1).getDay() + 6) % 7;

  const changeMonth = (delta: number) =>
    setView((current) => {
      const next = new Date(current.year, current.month + delta, 1);
      return { year: next.getFullYear(), month: next.getMonth() };
    });

  const cells: (number | null)[] = [
    ...Array.from({ length: firstOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => changeMonth(-1)}
          disabled={!canPrev}
          className="rounded p-1 hover:bg-zinc-100 disabled:opacity-30"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold">
          {MONTHS[view.month]} {view.year}
        </span>
        <button
          type="button"
          onClick={() => changeMonth(1)}
          disabled={!canNext}
          className="rounded p-1 hover:bg-zinc-100 disabled:opacity-30"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs text-cn-muted-foreground">
        {WEEKDAYS.map((weekday) => (
          <div key={weekday} className="py-1 font-medium">
            {weekday}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, index) => {
          if (day === null) return <div key={`empty-${index}`} />;
          const key = toKey(view.year, view.month, day);
          const disabled = key < minKey || key > maxKey;
          const selected = key === selectedDate;
          const hasPhoto = assignedDates.has(key);
          return (
            <button
              key={key}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(key)}
              className={cn(
                'relative flex h-9 items-center justify-center rounded text-sm',
                disabled && 'cursor-not-allowed text-zinc-300',
                !disabled && !selected && 'hover:bg-zinc-100',
                selected && 'bg-zinc-900 text-white',
              )}
            >
              {day}
              {hasPhoto && (
                <span
                  className={cn(
                    'absolute bottom-1 h-1 w-1 rounded-full',
                    selected ? 'bg-white' : 'bg-emerald-500',
                  )}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;
